import { useCallback, useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import { SSE } from "sse.js";

import { getIdToken } from "@needl-ai/common/auth/auth";
import { toast } from "@needl-ai/common/components/ui/sonner";
import { isMSquaredHub } from "@needl-ai/common/utils/isEnv";
import { getRequestOrigin } from "@needl-ai/common/utils/requestOrigin";
import {
  getActiveWorkspaceId,
  getWorkspaceFromUrl,
  useGetPathPrefix,
} from "@needl-ai/common/workspace";

import type { Message } from "@/types/chat";
import type { SSEvent } from "sse.js";

import {
  useInvalidateAllSessionsInfos,
  useInvalidateSession,
} from "@/hooks/useCreateUpdateSession";
import { getPendingRequestId } from "@/utils/messageUtils";

type BackgroundSession = {
  requestId: string;
  sessionId: string;
  title: string;
  sessionPath?: string;
};

export type AsyncAnswerStatus = "in_progress" | "completed";

export const useAsyncSessionTracker = (
  currentSessionId: string,
  messages: Message[],
  isActiveRequest: boolean
) => {
  const navigate = useNavigate();
  const pathPrefix = useGetPathPrefix();
  const invalidateAllSessions = useInvalidateAllSessionsInfos();
  const invalidateSession = useInvalidateSession();
  const currentSessionIdRef = useRef(currentSessionId);
  currentSessionIdRef.current = currentSessionId;
  const activeConnectionsRef = useRef<Map<string, InstanceType<typeof SSE>>>(
    new Map()
  );
  const backgroundSessionsRef = useRef<Map<string, BackgroundSession>>(
    new Map()
  );
  const [asyncStatuses, setAsyncStatuses] = useState<
    Map<string, AsyncAnswerStatus>
  >(new Map());

  // Clear status when user navigates to a session (auto-fade on visit)
  useEffect(() => {
    setAsyncStatuses((prev) => {
      if (!prev.has(currentSessionId)) return prev;
      const next = new Map(prev);
      next.delete(currentSessionId);
      return next;
    });
  }, [currentSessionId]);

  useEffect(() => {
    const connections = activeConnectionsRef.current;
    return () => {
      connections.forEach((sse) => sse.close());
      connections.clear();
    };
  }, []);

  const openSSE = useCallback(
    async (requestId: string, sessionId: string) => {
      if (activeConnectionsRef.current.has(requestId)) return;

      const token = (await getIdToken()) as string;
      if (!token) return;

      const headers: Record<string, string> = {
        token,
        "x-request-origin": getRequestOrigin(),
      };

      if (isMSquaredHub()) {
        const workspaceSubdomain = getWorkspaceFromUrl();
        if (workspaceSubdomain) {
          headers["x-workspace-subdomain"] = workspaceSubdomain;
        }
      } else {
        const workspaceId = getActiveWorkspaceId();
        if (workspaceId) {
          headers["x-workspace-id"] = workspaceId;
        }
      }

      const eventSource = new SSE(
        `${import.meta.env.VITE_API_URL}/chat/status/${requestId}`,
        {
          method: "GET",
          headers,
        }
      );

      activeConnectionsRef.current.set(requestId, eventSource);

      eventSource.onmessage = (event: SSEvent) => {
        if (!event.data || (event.data as string).startsWith("[")) return;
        try {
          const response = JSON.parse(event.data as string) as {
            status: string;
          };
          if (response.status === "KEEP_ALIVE") return;

          eventSource.close();
          activeConnectionsRef.current.delete(requestId);
          invalidateSession(sessionId);
          invalidateAllSessions();

          const bgSession = backgroundSessionsRef.current.get(requestId);
          if (
            bgSession &&
            response.status === "SUCCESS" &&
            currentSessionIdRef.current !== sessionId
          ) {
            setAsyncStatuses((prev) => {
              const next = new Map(prev);
              next.set(sessionId, "completed");
              return next;
            });

            const displayTitle =
              bgSession.title.length > 50
                ? bgSession.title.slice(0, 47) + "..."
                : bgSession.title;

            toast.success("Insights ready", {
              description: displayTitle,
              action: {
                label: "View answer →",
                onClick: () => {
                  const basePath = bgSession.sessionPath || "/c";
                  navigate(`${pathPrefix}${basePath}/${sessionId}`);
                },
              },
              cancel: {
                label: "Dismiss",
                onClick: () => {},
              },
              duration: 10000,
            });
          }
          backgroundSessionsRef.current.delete(requestId);

          // Clear in_progress status for non-SUCCESS terminals (failure/error cases)
          if (response.status !== "SUCCESS") {
            setAsyncStatuses((prev) => {
              if (!prev.has(sessionId)) return prev;
              const next = new Map(prev);
              next.delete(sessionId);
              return next;
            });
          }
        } catch {
          // ignore parse errors
        }
      };

      eventSource.onerror = () => {
        eventSource.close();
        activeConnectionsRef.current.delete(requestId);
        backgroundSessionsRef.current.delete(requestId);
        invalidateSession(sessionId);
        setAsyncStatuses((prev) => {
          if (!prev.has(sessionId)) return prev;
          const next = new Map(prev);
          next.delete(sessionId);
          return next;
        });
      };
    },
    [invalidateSession, invalidateAllSessions, navigate, pathPrefix]
  );

  // Auto-detect pending AI message in current session and open SSE.
  // Only runs when there's no active request (loading/streaming already handle that).
  useEffect(() => {
    if (isActiveRequest) return;
    const pendingId = getPendingRequestId(messages);
    if (!pendingId) return;
    openSSE(pendingId, currentSessionIdRef.current);
  }, [messages, isActiveRequest, openSSE]);

  const trackSession = useCallback(
    (session: BackgroundSession) => {
      backgroundSessionsRef.current.set(session.requestId, session);
      setAsyncStatuses((prev) => {
        const next = new Map(prev);
        next.set(session.sessionId, "in_progress");
        return next;
      });
      openSSE(session.requestId, session.sessionId);
    },
    [openSSE]
  );

  return { trackSession, asyncStatuses };
};
